
import AppHeader from "@/components/AppHeader.vue";
import Page from "@/components/Page.vue";
import { defineComponent, ref, watch, onMounted } from "vue";
import {
  email,
  helpers,
  maxValue,
  minValue,
  required,
} from "@vuelidate/validators";
import { useVuelidate } from "@vuelidate/core";
import { useResult } from "@vue/apollo-composable";
import {
  useGetSettingByKeyQuery,
  useUpdateSettingMutation,
  useAddNotificationEmailMutation,
  useDeleteNotificationEmailMutation,
  useGetNotificationEmailsQuery,
  NotificationOptionsType,
  SettingItemType,
} from "@/graphql/types";
import useToastService from "@/composables/useToastService";
import Error from "@/components/Error.vue";
import FormErrors from "@/forms/FormErrors.vue";
import gql from "graphql-tag";
import useError from "@/composables/useError";
import { useRouter } from "vue-router";
import NotificationOptionsModal from "@/components/modals/NotificationOptionsModal.vue";
import VDModal from "@/components/modals/VDModal";
gql`
  query GetNotificationEmails {
    notificationEmails {
      count
      items {
        ...NotificationEmailParts
      }
    }
  }
`;

gql`
  mutation AddNotificationEmail($input: AddNotificationEmailInputType!) {
    addNotificationEmail(input: $input) {
      ...NotificationEmailParts
    }
  }
`;

gql`
  mutation DeleteNotificationEmail($emailId: ID!) {
    deleteNotificationEmail(emailId: $emailId)
  }
`;

gql`
  query GetSettingByKey($key: String!) {
    setting(key: $key) {
      ...SettingParts
    }
  }
`;

gql`
  mutation UpdateSetting($input: UpdateSettingInputType!) {
    updateSetting(input: $input) {
      ...SettingParts
    }
  }
`;

export default defineComponent({
  name: "SettingsPage",
  components: {
    AppHeader,
    Page,
    FormErrors,
    Error,
    NotificationOptionsModal,
  },
  setup() {
    const toastService = useToastService();
    const router = useRouter();

    const deactivateUserAfterInDays = ref<number>(0);
    const keyAutoDisableInactiveUserDuration =
      "AutoDisableInactiveUserDuration";
    const {
      result: settingResult,
      loading: settingLoading,
      error: settingError,
      refetch: refetchSetting,
    } = useGetSettingByKeyQuery({
      key: keyAutoDisableInactiveUserDuration,
    });

    const deleteUserAfterAdditionalInDays = ref<number>(0);
    const keyAutoDeleteInactiveUserAfterDays =
      "AutoDeleteInactiveUserAfterDays";
    const {
      result: deletionSettingResult,
      loading: deletionSettingLoading,
      error: deletionSettingError,
      refetch: refetchDeletionSetting,
    } = useGetSettingByKeyQuery({
      key: keyAutoDeleteInactiveUserAfterDays,
    });

    const newEmail = ref<string>("");
    const deleteEmail = ref<string>("");

    const settingData = useResult(settingResult, null, (data) => {
      if (
        settingError.value &&
        settingError.value.message.includes("Entity was not found")
      ) {
        return { value: "0" }; // Default value when setting doesn't exist
      }
      return (data as { setting: any }).setting;
    });

    const deletionSettingData = useResult(
      deletionSettingResult,
      null,
      (data) => {
        if (
          deletionSettingError.value &&
          deletionSettingError.value.message.includes("Entity was not found")
        ) {
          return { value: "0" }; // Default value when setting doesn't exist
        }
        return (data as { setting: any }).setting;
      }
    );

    // Custom email validator with custom message
    const customEmail = helpers.withMessage(
      "Værdien er ikke en gyldig e-mailadresse", // Custom Danish message
      email
    );

    const customRequiredValue = helpers.withMessage(
      "Værdi er påkrævet", // Custom Danish message
      required
    );

    const customMinValue = helpers.withMessage(
      "Den mindste tilladte værdi er 0", // Custom Danish message
      minValue(0)
    );

    const customMaxValue = helpers.withMessage(
      "Den maksimale værdi er 9999", // Custom Danish message
      maxValue(9999)
    );

    const rules = {
      newEmail: { email: customEmail },
      deactivateUserAfterInDays: {
        required: customRequiredValue,
        minValue: customMinValue,
        maxValue: customMaxValue,
      },
      deleteUserAfterAdditionalInDays: {
        required: customRequiredValue,
        minValue: customMinValue,
        maxValue: customMaxValue,
      },
    };

    const vueValidation$ = useVuelidate(rules, {
      newEmail,
      deactivateUserAfterInDays,
      deleteUserAfterAdditionalInDays,
    });

    function handleDeactivateUserAfterInDaysInput() {
      vueValidation$.value.deactivateUserAfterInDays.$touch();
    }

    function handleDeleteUserAfterAdditionalInDaysInput() {
      vueValidation$.value.deleteUserAfterAdditionalInDays.$touch();
    }

    function handleInputEmail() {
      vueValidation$.value.newEmail.$touch();
    }

    function onUpdateDeactivateSettingBlur() {
      if (
        !vueValidation$.value.deactivateUserAfterInDays.$pending &&
        !vueValidation$.value.deactivateUserAfterInDays.$error &&
        deactivateUserAfterInDays.value !==
          parseInt(settingData.value?.value ?? "0")
      ) {
        onUpdateDeactivateSetting();
      }
    }

    function onUpdateDeletionSettingBlur() {
      if (
        !vueValidation$.value.deleteUserAfterAdditionalInDays.$pending &&
        !vueValidation$.value.deleteUserAfterAdditionalInDays.$error &&
        deleteUserAfterAdditionalInDays.value !==
          parseInt(deletionSettingData.value?.value ?? "0")
      ) {
        onUpdateDeletionSetting();
      }
    }

    const updateDeactivateSettingMutation = useUpdateSettingMutation({});
    updateDeactivateSettingMutation.onDone(() => {
      refetchSetting();
      toastService.show({
        severity: "success",
        title: "Indstillinger opdateret",
        message:
          "Indstillingen 'Automatisk deaktivér bruger efter' er blevet opdateret",
      });
    });
    updateDeactivateSettingMutation.onError((error) =>
      toastService.show({ severity: "error", error: useError(error) })
    );

    const updateDeletionSettingMutation = useUpdateSettingMutation({});
    updateDeletionSettingMutation.onDone(() => {
      refetchDeletionSetting();
      toastService.show({
        severity: "success",
        title: "Indstillinger opdateret",
        message:
          "Indstillingen 'Automatisk slet bruger efter yderligere' er blevet opdateret",
      });
    });
    updateDeletionSettingMutation.onError((error) =>
      toastService.show({ severity: "error", error: useError(error) })
    );

    const onUpdateDeactivateSetting = async () => {
      try {
        updateDeactivateSettingMutation.mutate({
          input: {
            key: keyAutoDisableInactiveUserDuration,
            value: deactivateUserAfterInDays.value.toString(),
            settingItemType: SettingItemType.AutoDisableInactiveUserDuration,
          },
        });
      } catch (error) {
        console.error(error);
      }
    };

    const onUpdateDeletionSetting = async () => {
      try {
        updateDeletionSettingMutation.mutate({
          input: {
            key: keyAutoDeleteInactiveUserAfterDays,
            value: deleteUserAfterAdditionalInDays.value.toString(),
            settingItemType: SettingItemType.AutoDeleteInactiveUserAfterDays,
          },
        });
      } catch (error) {
        console.error(error);
      }
    };

    watch(
      settingData,
      (newVal) => {
        if (newVal) {
          if (newVal.value !== "") {
            deactivateUserAfterInDays.value = parseInt(newVal.value);
          } else {
            deactivateUserAfterInDays.value = 0;
          }
        }
      },
      { immediate: true }
    );

    watch(
      deletionSettingData,
      (newVal) => {
        if (newVal) {
          if (newVal.value !== "") {
            deleteUserAfterAdditionalInDays.value = parseInt(newVal.value);
          } else {
            deleteUserAfterAdditionalInDays.value = 0;
          }
        }
      },
      { immediate: true }
    );

    const {
      result: emailResult,
      loading: emailLoading,
      error: emailError,
      refetch: refetchEmails,
    } = useGetNotificationEmailsQuery({});

    const addNotificationEmailMutation = useAddNotificationEmailMutation({});

    addNotificationEmailMutation.onDone(() => {
      const addedEmail = newEmail.value;
      newEmail.value = "";
      refetchEmails();
      toastService.show({
        severity: "success",
        title: "Notifikationsliste tilføjelse",
        message: `'${addedEmail}' blev tilføjet til notifikationslisten`,
      });
    });

    const onAddNotificationEmail = async () => {
      try {
        addNotificationEmailMutation.mutate({
          input: {
            emailAddress: newEmail.value,
          },
        });
      } catch (error) {
        console.error(error);
      }
    };

    const deleteNotificationEmailMutation = useDeleteNotificationEmailMutation(
      {}
    );

    deleteNotificationEmailMutation.onDone(() => {
      const removed = deleteEmail.value;
      deleteEmail.value = "";
      refetchEmails();
      toastService.show({
        severity: "success",
        title: "Notifikationsliste fjernelse",
        message: `'${removed}' blev fjernet fra notifikationslisten`,
      });
    });

    const onDeleteNotificationEmail = async (data: any) => {
      try {
        deleteEmail.value = data.emailAddress ?? null;
        deleteNotificationEmailMutation.mutate({
          emailId: data.emailId,
        });
      } catch (error) {
        console.error(error);
      }
    };

    // Set up component when mounted
    onMounted(() => {
      refetchSetting();
      refetchDeletionSetting();
      refetchEmails();
    });

    // Watch for route changes to reload data when navigating back to Settings
    watch(
      () => router.currentRoute.value.fullPath,
      (newPath) => {
        if (
          newPath.includes("/settings") ||
          newPath.includes("/indstillinger")
        ) {
          setTimeout(() => {
            refetchSetting();
            refetchDeletionSetting();
            refetchEmails();
          }, 100);
        }
      }
    );

    const notificationOptionsModal =
      ref<VDModal<NotificationOptionsType> | null>(null);

    function onReloadEmailsNotification() {
      refetchEmails();
    }

    return {
      error: useError(settingError || emailError),
      loading: settingLoading && emailLoading,
      vueValidation$,
      deactivateUserAfterInDays,
      deleteUserAfterAdditionalInDays,
      handleDeactivateUserAfterInDaysInput,
      handleDeleteUserAfterAdditionalInDaysInput,
      onUpdateDeactivateSettingBlur,
      onUpdateDeletionSettingBlur,
      notificationEmails: useResult(emailResult),
      newEmail,
      onAddNotificationEmail,
      onDeleteNotificationEmail,
      handleInputEmail,
      notificationOptionsModal,
      onReloadEmailsNotification,
    };
  },
});
